// Which columns the queue shows, and in what order.
//
// TWO HALVES, DELIBERATELY SPLIT. The CATALOGUE is here: every column the queue knows
// how to render, with its label and its width. The SELECTION is data — an ordered array
// of keys on `saved_views.columns_json` — so an admin arranges the queue on the views
// screen and every agent who opens that view sees the arrangement.
//
// The catalogue cannot be data, because each column needs a renderer that knows how to
// draw a status badge or an SLA clock. A key with no renderer is the one failure mode
// worth guarding: `visibleColumns` drops anything it does not recognise rather than
// rendering a hole, so a view saved by a newer build stays usable on an older one, and
// a column removed from the product does not empty the queue for everyone who had it
// selected.
//
// WIDTHS STAY IN CODE. They are `table-fixed` percentages tuned against each other and
// against the row height, not preferences — a width picker would let an admin produce a
// table whose columns sum to more than 100% and whose subject column then truncates to
// nothing. What an admin chooses is which columns and in what order; the widths follow.

/**
 * Every column the queue can render, in its default order.
 *
 * `key` is what `columns_json` stores and what the cell map in List.jsx is keyed on.
 * The two must agree: a key here with no cell renders nothing at all, which under
 * `table-fixed` is an empty column rather than an error.
 */
export const TICKET_COLUMNS = Object.freeze([
  { key: 'ref', label: 'Ref', width: 'w-[4.5rem]' },
  { key: 'subject', label: 'Subject', width: 'w-[26%]' },
  { key: 'sla', label: 'Reply due', width: 'w-[7rem]' },
  { key: 'customer', label: 'Customer', width: 'w-[17%]' },
  { key: 'organization', label: 'Organization', width: 'w-[13%]' },
  { key: 'status', label: 'Status', width: 'w-[7rem]' },
  { key: 'priority', label: 'Priority', width: 'w-[7rem]' },
  { key: 'assignee', label: 'Assignee', width: 'w-[13%]' },
  { key: 'channel', label: 'Channel', width: 'w-[7rem]' },
  { key: 'updated', label: 'Updated', width: 'w-[9rem]' },
]);

/** The keys an admin may choose from, for the picker and for server-side validation. */
export const TICKET_COLUMN_KEYS = Object.freeze(TICKET_COLUMNS.map((c) => c.key));

const BY_KEY = new Map(TICKET_COLUMNS.map((c) => [c.key, c]));

/**
 * A column nobody can remove.
 *
 * Every row's whole purpose is to be opened, and both link cells are the subject and the
 * reference. A view saved with neither is a list of rows with nothing to click, which
 * reads as a broken screen rather than as a deliberate arrangement — so if a selection
 * contains neither, the default is used instead of honouring it.
 */
const OPENABLE = ['ref', 'subject'];

/**
 * The columns to render, resolved from a view's stored selection.
 *
 * `selected` is `saved_views.columns_json`: an ordered array of keys, or empty/absent
 * for "no arrangement saved", which is every view until somebody arranges one. Both
 * fall back to the full catalogue in its default order, so the queue an agent already
 * knows is what they keep getting.
 *
 * @param {string[]|null|undefined} selected
 * @returns {Array<{key: string, label: string, width: string}>} never empty
 */
export function visibleColumns(selected) {
  if (!Array.isArray(selected) || selected.length === 0) return [...TICKET_COLUMNS];

  const seen = new Set();
  const chosen = [];
  for (const key of selected) {
    const column = BY_KEY.get(String(key));
    // Unknown keys are DROPPED, not rendered as a blank column: a view written by a
    // newer build, or naming a column since removed, stays usable instead of emptying
    // the queue for everyone who selected it.
    if (!column || seen.has(column.key)) continue;
    seen.add(column.key);
    chosen.push(column);
  }

  // An EMPTY result needs no branch of its own: an empty array has nothing openable in
  // it either, so the check below returns the default for it. Measured — a separate
  // emptiness guard here was unfailable, and a guard nothing can fail is one that gets
  // deleted in a later refactor with a green suite to justify it.
  //
  // A list of rows with nothing to click reads as a broken screen. Rather than silently
  // adding a column the admin did not choose — which would make the picker lie — the
  // whole arrangement is refused and the default stands.
  if (!chosen.some((c) => OPENABLE.includes(c.key))) return [...TICKET_COLUMNS];
  return chosen;
}

/**
 * Whether a stored selection would actually be honoured.
 *
 * The admin picker uses this to say so BEFORE saving. A selection that silently falls
 * back is the worst outcome of this feature: the admin arranges the queue, saves, and
 * every agent keeps seeing the old one with nothing to explain why.
 *
 * @returns {{ok: true} | {ok: false, reason: string}}
 */
export function selectionProblem(selected) {
  if (!Array.isArray(selected) || selected.length === 0) {
    return { ok: false, reason: 'Nothing selected, so this view uses the default columns.' };
  }
  const known = selected.map(String).filter((key) => BY_KEY.has(key));
  if (known.length === 0) {
    return { ok: false, reason: 'None of these columns exist, so the default is used.' };
  }
  if (!known.some((key) => OPENABLE.includes(key))) {
    return {
      ok: false,
      reason: 'Include Ref or Subject — without one there is nothing to click through to, '
        + 'so the default columns are used instead.',
    };
  }
  return { ok: true };
}
